import React, { useState } from 'react';
import { QcRequestState } from './qcRequestState';
import { Alert, Box, Button, Chip, TextField, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Translate } from 'react-localize-redux';
import Modal from '../../../components/general/modal';
import ShotTypeLabel from './ShotTypeLabel';
import { correctedIdentifiable } from './imageMeta';
import { AUDIT_TINT } from './qcTokens';

/**
 * The handoff disables confirm until the reason has six characters, not one.
 *
 * That is a deliberately low bar with a real purpose: it rules out "ok", "n/a" and a
 * stray keystroke, which are the reasons a required free-text field actually collects
 * once it is only checked for emptiness. This one is read by whoever opens the visit
 * next, off a permanent audit line.
 */
export const MIN_REASON_LENGTH = 6;

interface CorrectIdentifiableModalProps {
    open: boolean;
    /**
     * Which photograph, named exactly as its card names it — the eCRF field the shot
     * hangs off, and the lesion's body region when the form recorded one. The dialog
     * covers the card it refers to, so it has to name its subject; the handoff opens
     * the sentence with it.
     *
     * The field name rather than a formatted string, so the naming stays in one place:
     * `ShotTypeLabel` is the card's and this reuses it. A caller-formatted label would
     * be the second answer to "what is this photograph called".
     */
    fieldName: string;
    bodyRegion: string | null;
    /**
     * The flag as it stands, which decides the only correction on offer. `null` — the
     * device recorded no opinion — is treated as "not flagged": silence is not an
     * assurance that a patient is unidentifiable in their own photograph, so the
     * correction worth offering is the one that adds the claim.
     */
    currentIdentifiable: boolean | null;
    /**
     * Where the correction request has got to, as distinct from what the flag currently is —
     * the flag belongs to the image and the server owns it.
     */
    request: QcRequestState;
    onConfirm: (reason: string) => void;
    onCancel: () => void;
}

/**
 * SKIN-243 — correcting one photograph's identifiable flag.
 *
 * Presentational and controlled: it holds the reason being typed and nothing else. The
 * request, the endpoint and the audit trail belong to the container, which is what lets
 * every state below exist in the catalog.
 *
 * Built to the design handoff (`design_handoff_quality_check`, "Correction modal"): the
 * audit tag above the title, one title for both directions with the direction stated in
 * the body, a required reason with the direction's own example, and the note about what
 * the record does and does not do. The dialog chrome is the codebase's shared `Modal`,
 * as the handoff asks — "use the codebase's component and apply these tokens, do not
 * fork new primitives".
 */
export default function CorrectIdentifiableModal({
    open,
    fieldName,
    bodyRegion,
    currentIdentifiable,
    request,
    onConfirm,
    onCancel,
}: CorrectIdentifiableModalProps) {
    const [reason, setReason] = useState('');

    // The same helper the container builds the request body from, so the direction stated
    // here and the value actually written can never disagree.
    const direction = correctedIdentifiable(currentIdentifiable) ? 'to-identifiable' : 'to-non-identifiable';
    const submitting = request.status === 'submitting';
    // Trimmed, because the server trims before it checks — accepting a space here would
    // send a request we already know is refused.
    const canConfirm = reason.trim().length >= MIN_REASON_LENGTH && !submitting;
    /**
     * The button says what is happening, because nothing else on this dialog does.
     * Disabling confirm and greying the field left the in-flight state visually identical
     * to an untouched form — a reviewer would press Save, see nothing move, and press
     * again. The rail announces its own in-flight step the same way.
     */
    const confirmLabelId = submitting
        ? 'hospital.quality-check.review.correcting'
        : 'hospital.quality-check.review.correct-confirm';

    return (
        <Modal
            open={open}
            title={<CorrectionTitle />}
            closeModal={onCancel}
            maxWidth="sm"
            fullWidth
        >
            <Box data-testid="correct-identifiable-modal" data-direction={direction} sx={{ width: '100%' }}>
                {/*
                  * The photograph, then what is happening to it. The label leads the
                  * sentence rather than being interpolated into it: this codebase uses no
                  * `Translate` placeholders (see ImageReviewCard), and a leading subject
                  * reads correctly in all three languages.
                  */}
                <Typography variant="body2" color="text.secondary" data-testid="correct-consequence">
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        <ShotTypeLabel fieldName={fieldName} />
                        {bodyRegion ? ` \u00b7 ${bodyRegion}` : ''}
                    </Box>
                    {' '}
                    <Translate id={`hospital.quality-check.review.correct-body.${direction}`} />
                </Typography>

                <Box sx={{ mt: 1.75 }}>
                    <Typography
                        component="label"
                        htmlFor="correct-identifiable-reason"
                        variant="caption"
                        display="block"
                        sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}
                    >
                        <Translate id="hospital.quality-check.review.correct-reason-label" />
                        <Box component="span" sx={{ color: 'error.main' }}> *</Box>
                    </Typography>

                    {/* The render prop, as CategoryFeedback does it: a placeholder is an
                      * attribute, so it needs the string rather than the element. The
                      * example is the direction's own — "no face visible" is not a reason
                      * for flagging one. */}
                    <Translate>
                        {({ translate }) => (
                            <TextField
                                id="correct-identifiable-reason"
                                data-testid="correct-reason"
                                placeholder={String(translate(
                                    `hospital.quality-check.review.correct-reason-placeholder.${direction}`,
                                ))}
                                multiline
                                minRows={3}
                                fullWidth
                                required
                                disabled={submitting}
                                value={reason}
                                onChange={(event) => setReason(event.target.value)}
                            />
                        )}
                    </Translate>

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        <Translate id="hospital.quality-check.review.correct-audit-note" />
                    </Typography>
                </Box>

                {request.status === 'failed' && (
                    // The reason survives this: making a reviewer retype a justification
                    // because the network dropped is how a reviewer learns to write "n/a".
                    <Alert severity="error" data-testid="correct-failed" sx={{ mt: 2 }}>
                        <Translate id="hospital.quality-check.review.correct-failed" />
                        {/*
                          * The server's own words, as the rail's approve-failed banner shows
                          * them. Without this the arm carried an `error` string that was
                          * computed on every failure and rendered nowhere, so a reviewer
                          * refused for a reason the API stated — a permission, a locked
                          * visit — read the same as a dropped connection.
                          */}
                        {request.error && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {request.error}
                            </Typography>
                        )}
                    </Alert>
                )}

                {/*
                  * The handoff's action row — cancel, then a filled confirm carrying a
                  * check — rather than the shared Modal's own, whose confirm is a text
                  * button and whose cancel is red. Red is the colour this product uses for
                  * destruction, and nothing here is destroyed: the correction keeps the
                  * image, the data and the original call.
                  *
                  * Built from the same `Button` and theme colour as the rail's Approve two
                  * panels away, not from the handoff's raw teal, so the two primary actions
                  * on this screen cannot drift apart.
                  */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, mt: 2.5 }}>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={onCancel}
                        data-testid="correct-cancel"
                        sx={{ fontWeight: 600 }}
                    >
                        <Translate id="general.cancel" />
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!canConfirm}
                        onClick={() => onConfirm(reason.trim())}
                        data-testid="correct-confirm"
                        startIcon={<CheckIcon />}
                        sx={{ fontWeight: 600 }}
                    >
                        <Translate id={confirmLabelId} />
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}

/**
 * The tag, then the title — the handoff's header, in that order.
 *
 * The tag is the honest part of this dialog: the correction edits the source record and
 * is audited, and a reviewer deserves to know that before they read the question, not
 * after they answer it.
 */
function CorrectionTitle() {
    return (
        <Box>
            <Chip
                size="small"
                icon={<EditOutlinedIcon fontSize="small" />}
                data-testid="correct-audit-tag"
                label={<Translate id="hospital.quality-check.review.correct-tag" />}
                sx={{ bgcolor: AUDIT_TINT.bg, color: AUDIT_TINT.fg, fontWeight: 600 }}
            />
            <Typography variant="h6" component="h3" sx={{ mt: 1.5, fontWeight: 600 }} data-testid="correct-title">
                <Translate id="hospital.quality-check.review.correct-title" />
            </Typography>
        </Box>
    );
}
